import { useEffect } from "react"
import { userData } from "@signals"
import { pushRight } from "@app/@signals"
import { dsColorNew } from "@palette"
import { USER_ROUTES } from "@constants"
import { clearIrisProfile, irisProfile, irisProfileState, irisPrompt, loadIrisProfile } from "@signals/irisProfile"
import { pushIrisProfile, welcomeMarkdown } from "@utils/irisProfile"

/**
 * Loads the rentacar-pms-agent chat widget script, dressed as Iris.
 *
 * IMPORTANT — access control:
 *   The widget is ONLY loaded for the users in `ALLOWED_USERS`. This is
 *   a first-line, UX-level gate; the widget's own backend
 *   (rentacar-pms-agent-*.run.app) MUST validate the incoming
 *   `data-username` against a server-side allowlist before returning
 *   any data — the DOM-side check can be bypassed by anyone with
 *   devtools.
 *
 * Behaviour:
 *   - No-op when the user is not logged in or is not in `ALLOWED_USERS`.
 *   - When it applies, appends one <script> to <body> with the
 *     data-app / data-username attributes the widget expects, plus the
 *     look: no bubble of its own (the side menu's IrisLauncher opens it),
 *     a right drawer, the Iris mark and the app's blue.
 *   - Removes the script on unmount OR when the user changes / logs
 *     out, so switching users tears the widget down.
 *
 * The user's Iris profile (CU-869fkmt6r) — this is the only place that
 * talks to the widget about it:
 *   - The script is injected once `/api/ia-workspace/profile` answers (or
 *     fails), or after PROFILE_WAIT_MS if it hangs, so `data-welcome` knows
 *     whether to greet with "Set up Iris" (nothing of the user's saved) or
 *     leave the agent's own welcome. Read at mount only: no remount after
 *     the first Save — the launcher changes live, the welcome on the next
 *     page load (criterion 14c).
 *   - The saved profile is pushed with `DSB.setContext({ profile })` when it
 *     loads, on `dsb:ready` and after every Save (the signal changes);
 *     `{ profile: null }` before the script goes on logout (criterion 18b).
 */
// The canonical Cloud Run URL of the service (the agents team, 2026-10-21); widget 0.109.4+ (`setContext`, `data-welcome`)
const AGENT_SRC       = 'https://rentacar-pms-agent-zt5eyetlyq-uc.a.run.app/widget.js'
// Keep in sync with webback's IRIS_PILOT_USERS (app/blueprints/ia_workspace/services/_base.py): same list, both sides
export const ALLOWED_USERS = ['canarias-revenue', 'europcar-kracon']
const SCRIPT_ELEMENT_ID = 'rentacar-pms-agent-widget'
// The static mark (same drawing as @components/brand/IrisMark); `public/assets` is the
// tracked, served folder — `public/static` is build output and wiped by `npm run clean`
const IRIS_MARK_PATH = '/assets/iris.svg'

// A hung `/profile` must not leave the app without Iris: mount anyway after this long
const PROFILE_WAIT_MS = 8000
const IRIS_SETTINGS_URL = USER_ROUTES.IRIS_PATH

export const isIrisUser = username => ALLOWED_USERS.includes(String(username ?? '').trim().toLowerCase())

export default function PmsAgentWidget() {
	const rawUsername = userData.value?.username ?? ''
	const isAllowed = isIrisUser(rawUsername)
	const profile = irisProfile.value
	const prompt = irisPrompt.value

	useEffect(() => {
		if (!isAllowed) {
			// Defensive: if a previous session as the allowed user left
			// the script behind, tear it down when a different user logs in —
			// and nothing of theirs travels any more (criterion 18b).
			pushIrisProfile(window.DSB, null)
			clearIrisProfile()
			const stale = document.getElementById(SCRIPT_ELEMENT_ID)
			if (stale && stale.parentNode) stale.parentNode.removeChild(stale)
			return undefined
		}

		if (document.getElementById(SCRIPT_ELEMENT_ID)) return undefined

		let cancelled = false
		let observer = null
		const mount = () => {
			if (cancelled || document.getElementById(SCRIPT_ELEMENT_ID)) return
			clearTimeout(wait)
			observer = injectWidget(rawUsername)
		}
		const wait = setTimeout(mount, PROFILE_WAIT_MS)
		loadIrisProfile().finally(mount)

		return () => {
			cancelled = true
			clearTimeout(wait)
			// drop the profile before the widget goes, then forget it: the next user starts clean
			pushIrisProfile(window.DSB, null)
			clearIrisProfile()
			if (observer) observer.disconnect()
			pushRight.value = 0
			const el = document.getElementById(SCRIPT_ELEMENT_ID)
			if (el && el.parentNode) el.parentNode.removeChild(el)
		}
	}, [isAllowed, rawUsername])

	// The saved profile on every question: now, when the widget says it is ready (the script is async,
	// `window.DSB` may not exist yet), and whenever the signal changes (a Save, Account's refresh)
	useEffect(() => {
		if (!isAllowed || profile === null) return undefined
		const push = () => pushIrisProfile(window.DSB, profile, prompt)
		push()
		document.addEventListener('dsb:ready', push)
		return () => document.removeEventListener('dsb:ready', push)
	}, [isAllowed, profile, prompt])

	return null
}

// Appends the widget's <script> and returns the observer that mirrors its push margin
function injectWidget(rawUsername) {
	const irisMarkUrl = `${window.location.origin}${IRIS_MARK_PATH}`
	const script = document.createElement('script')
	script.id           = SCRIPT_ELEMENT_ID
	script.src          = AGENT_SRC
	script.async        = true
	script.setAttribute('data-app', 'rentacarpms')
	script.setAttribute('data-username', rawUsername)  // pass verbatim, incl. case
	// Launcher: none — the side menu's IrisLauncher drives it through window.DSB
	script.setAttribute('data-launcher', 'none')
	script.setAttribute('data-layout', 'drawer')
	script.setAttribute('data-position', 'right')
	// `true`: the drawer narrows the page (margin on <html>) instead of covering it (Curro, 2026-10-21)
	script.setAttribute('data-push', 'true')
	script.setAttribute('data-title', 'Iris')
	script.setAttribute('data-icon-url', irisMarkUrl)
	script.setAttribute('data-avatar', irisMarkUrl)
	script.setAttribute('data-accent', dsColorNew.webBlue500)
	script.setAttribute('data-radius', 'round')

	// The agent's default drops the whole answer at once; reveal it progressively instead (guide § 7).

	// `data-stream="true"` would show tokens as they arrive, but the brain abandons drafts mid-way

	// (`answer_reset`, PRD-ia-workspace § 7k) — the typed-out finished answer is the honest one.

	script.setAttribute('data-typewriter', 'true')

	// First use: Iris's own welcome with the way to Settings › Iris, until something of the user's is saved
	const welcome = welcomeMarkdown({ profile: irisProfile.value, state: irisProfileState.value, settingsPath: IRIS_SETTINGS_URL })
	if (welcome) script.setAttribute('data-welcome', welcome)
	document.body.appendChild(script)

	// `data-push` sets `margin-right` on <html> (drawer open, or resized by its grip); the
	// app's fixed header and bars span the viewport, so they would sit behind the drawer
	// (Curro, 2026-10-21). Mirror that margin into a signal the fixed elements subtract.
	const html = document.documentElement
	const sync = () => { pushRight.value = parseInt(html.style.marginRight, 10) || 0 }
	const observer = new MutationObserver(sync)
	observer.observe(html, { attributes: true, attributeFilter: ['style', 'class'] })
	sync()

	return observer
}
